import { useEffect } from 'react';

import { buildClientSchema, getIntrospectionQuery, type GraphQLSchema } from 'graphql';
import { useAtomValue, useSetAtom } from 'jotai';

import { DEFAULT_TENANT_BASE_URL } from '../pages/labs/home/constants';

import { getGraphQLEndpointUrl, JSON_CONTENT_TYPE } from './graphqlEndpoints';
import {
	GraphQLSchemaErrorAtom,
	GraphQLSchemaLoadingAtom,
	GraphQLSchemaReloadKeyAtom,
	GraphQLSchemaTypesByNameAtom,
	GraphQLSchemaVariantAtom,
} from './GraphQLSchemaAtoms';
import { toTypesByNameFromSchema } from './GraphQLSchemaParsing';

async function getAuthHeaders(): Promise<Record<string, string>> {
	return {};
}

function normalizeBase(url: string) {
	return url.replace(/\/$/, '');
}

/**
 * Fetches the GraphQL introspection schema from the given base URL and path.
 * Handles local-dev proxy routing and includes a 10-second timeout.
 */
async function fetchIntrospectionForPath(
	baseUrl: string,
	path: string = '/gateway/api/graphql',
): Promise<any> {
	const headers = await getAuthHeaders();
	const url = getGraphQLEndpointUrl(path, normalizeBase(baseUrl));

	// Create a promise that rejects after 10 seconds
	const timeoutPromise = new Promise((_, reject) => {
		setTimeout(() => reject(new Error('Introspection query timeout after 10 seconds')), 10000);
	});

	// Race between the fetch and the timeout
	const res = (await Promise.race([
		fetch(url, {
			method: 'POST',
			credentials: 'include',
			mode: 'cors',
			headers: {
				'Content-Type': JSON_CONTENT_TYPE,
				...headers,
			},
			body: JSON.stringify({
				query: getIntrospectionQuery({
					descriptions: true,
					directiveIsRepeatable: true,
					schemaDescription: true,
					inputValueDeprecation: true,
				}),
			}),
		}),
		timeoutPromise,
	])) as Response;

	const json = await res.json();

	if (!res.ok || json.errors) {
		throw new Error(`Introspection failed: ${JSON.stringify(json.errors || res.statusText)}`);
	}
	return json.data;
}

export function useGraphQLSchemaLoader(): void {
	// Always use DEFAULT_TENANT_BASE_URL for schema loading
	const baseUrl = DEFAULT_TENANT_BASE_URL;
	const setLoadingAtom = useSetAtom(GraphQLSchemaLoadingAtom);
	const setErrorAtom = useSetAtom(GraphQLSchemaErrorAtom);
	const setTypesByNameAtom = useSetAtom(GraphQLSchemaTypesByNameAtom);
	const setVariantAtom = useSetAtom(GraphQLSchemaVariantAtom);
	const reloadKey = useAtomValue(GraphQLSchemaReloadKeyAtom);

	const isLoading = useAtomValue(GraphQLSchemaLoadingAtom);

	useEffect(() => {
		// If already loading, do nothing to prevent concurrent requests
		if (isLoading) {
			return;
		}

		let cancelled = false;

		async function run() {
			try {
				setLoadingAtom(true);
				setErrorAtom(null);

				let schema: GraphQLSchema | null = null;
				const introspectionData = await fetchIntrospectionForPath(baseUrl);

				if (cancelled) {
					return;
				}

				schema = buildClientSchema(introspectionData);

				if (schema) {
					try {
						const mapped = toTypesByNameFromSchema(schema);
						if (!cancelled) {
							setTypesByNameAtom(mapped);
							setVariantAtom('internal');
						}
					} catch (mapErr: any) {
						if (!cancelled) {
							const errorMsg = mapErr?.message || String(mapErr);
							setErrorAtom(errorMsg);
						}
					}
				}
			} catch (e: any) {
				if (cancelled) {
					return;
				}
				const errorMsg = e.message;
				setErrorAtom(errorMsg);
			} finally {
				if (!cancelled) {
					setLoadingAtom(false);
				}
			}
		}
		run();

		return () => {
			cancelled = true;
		};
		// React to changes in reloadKey (manual reloads)
		// baseUrl is always DEFAULT_TENANT_BASE_URL, so it doesn't need to be in deps
		// isLoading is checked inside the effect to prevent concurrent loads
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reloadKey]);
}
